import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePlan, getPlans, createIntent, setDefaultPaymentMethod } from '@app/services/plans';
import { getProfile } from '@app/services/user';
import { useAppDispatch } from '@app/redux/hooks';
import { ActionTypes } from '@app/redux/types';
import { toast } from 'react-toastify';
import { Stripe, StripeElements } from '@stripe/stripe-js';

interface PlanDetails {
  name: string;
  price: string;
  priceId: string;
  planName: string;
}

export const useTrialPaymentPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);
  const [clientSecret, setClientSecret] = useState<string>('');

  useEffect(() => {
    const initializePayment = async () => {
      try {
        const storedPlan = localStorage.getItem('selectedPlan');

        if (!storedPlan) {
          setIsLoading(false);
          navigate('/trial-welcome');
          return;
        }

        const planMapping: Record<string, string> = {
          starter: 'Free',
          professional: 'Pro',
          enterprise: 'Groth',
        };

        const [plansResponse, intentResponse] = await Promise.all([
          getPlans(),
          createIntent(),
        ]);

        const plans = plansResponse.data.plans;
        const planType = planMapping[storedPlan];
        const apiPlan = plans.find((plan: any) => plan.type === planType);

        if (!apiPlan) {
          throw new Error('Plan not found');
        }

        setPlanDetails({
          name: `${planType.charAt(0) + planType.slice(1).toLowerCase()} Plan`,
          price: `$${apiPlan.price}`,
          priceId: apiPlan.priceId,
          planName: apiPlan.type,
        });

        setClientSecret(intentResponse.data.intent);
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing payment:', error);
        toast.error('Error loading plan details');
        setIsLoading(false);
        navigate('/trial-welcome');
      }
    };

    initializePayment();
  }, [navigate]);

  const handleStripeSuccess = async (stripe: Stripe, elements: StripeElements) => {
    setIsProcessing(true);
    try {
      if (!planDetails || !clientSecret) {
        throw new Error('Missing payment details');
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        clientSecret,
        elements,
        confirmParams: {
          return_url: window.location.origin,
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment setup failed');
        setIsProcessing(false);
        return;
      }

      if (setupIntent?.payment_method) {
        const paymentMethodId = typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;
        await setDefaultPaymentMethod(paymentMethodId);
      }

      await updatePlan(planDetails.priceId);

      try {
        const userResponse = await getProfile();
        dispatch({ type: ActionTypes.AUTH_SAVE, payload: userResponse?.data?.user });
        dispatch({
          type: ActionTypes.BUSINESS_LOAD_LIST,
          payload: userResponse?.data?.businesses,
        });
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }

      localStorage.removeItem('selectedPlan');
      toast.success('Plan upgraded successfully!');

      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Error upgrading plan. Please try again.');
      setIsProcessing(false);
    }
  };

  return {
    planDetails,
    clientSecret,
    handleStripeSuccess,
    isProcessing,
    isLoading,
  };
};
